import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react'
import { searchLinkableTransactions, type LinkedTransactionOption } from '@/api/transactions'
import { type Category } from '@/api/categories'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { formatDate } from '@/lib/formatDate'
import { cn } from '@/lib/utils'
import { TONE_TILE, type KindTone } from './addKinds'

const LABEL_CLS = 'mb-1 block text-xs font-medium text-[var(--fl-text-2)]'

export function FieldError({ message }: { message?: string | null }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-[var(--fl-error)]">{message}</p>
}

export function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string | null
  children: ReactNode
}) {
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      {children}
      <FieldError message={error} />
    </div>
  )
}

/** The amount first and large: it's the one thing every entry has. */
export function AmountHero({
  label = 'Amount',
  error,
  children,
}: {
  label?: string
  error?: string | null
  children: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 pt-1">
      <label className="text-xs font-medium text-[var(--fl-text-2)]">{label}</label>
      <div className="w-full max-w-[260px] [&_input]:h-14 [&_input]:text-center [&_input]:font-serif [&_input]:text-[30px] [&_input]:font-semibold [&_input]:tabular-nums">
        {children}
      </div>
      <FieldError message={error} />
    </div>
  )
}

export function ChoiceChips<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: T; label: string }[]
  value: T | null
  onChange: (value: T) => void
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap justify-center gap-1.5">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'h-8 rounded-full border px-3 text-[13px] font-medium transition-colors',
              active
                ? 'border-[var(--fl-text-1)] bg-[var(--fl-text-1)] text-[var(--fl-surface)]'
                : 'border-[var(--fl-border)] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]'
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function InfoNote({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn'
  children: ReactNode
}) {
  const Icon = tone === 'warn' ? TriangleAlert : Info
  return (
    <p
      role={tone === 'warn' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-[13px] leading-snug',
        tone === 'warn'
          ? 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]'
          : 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
      )}
    >
      <Icon size={16} aria-hidden="true" className="mt-px shrink-0" />
      <span>{children}</span>
    </p>
  )
}

/** Rarely used fields (member, note, one-off…) stay out of the way until asked for. */
export function MoreSection({
  label,
  defaultOpen = false,
  children,
}: {
  label: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 items-center justify-between px-1 text-sm font-medium text-[var(--fl-primary)]"
      >
        {open ? 'Fewer options' : label}
        {open ? (
          <ChevronUp size={16} aria-hidden="true" />
        ) : (
          <ChevronDown size={16} aria-hidden="true" />
        )}
      </button>
      {open && <div className="flex flex-col gap-4">{children}</div>}
    </div>
  )
}

export function KindTile({
  Icon,
  tone,
  size = 'md',
}: {
  Icon: LucideIcon
  tone: KindTone
  size?: 'sm' | 'md'
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center',
        size === 'sm' ? 'h-[30px] w-[30px] rounded-[9px]' : 'h-9 w-9 rounded-[10px]',
        TONE_TILE[tone]
      )}
    >
      <Icon size={size === 'sm' ? 16 : 18} strokeWidth={1.9} />
    </span>
  )
}

/** A form screen's frame: full-screen on a phone (room for the keyboard), a dialog on desktop. */
export function FormDialog({
  label,
  onClose,
  children,
}: {
  label: string
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 md:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="flex h-full w-full flex-col overflow-hidden bg-[var(--fl-surface)] md:h-[min(820px,92vh)] md:w-[420px] md:rounded-[20px] md:shadow-[0_16px_60px_rgba(0,0,0,0.22)]"
      >
        {children}
      </div>
    </div>
  )
}

/**
 * One form screen: a header (back or close, the kind's tile and title), a scrolling body and a
 * footer with the submit button (wired to the body's form by id) and an optional Delete.
 */
export function FormShell({
  title,
  Icon,
  tone,
  onBack,
  onClose,
  formId,
  submitLabel,
  submitting,
  error,
  onDelete,
  children,
}: {
  title: string
  Icon: LucideIcon
  tone: KindTone
  /** Back to the chooser; without it the header offers Close. */
  onBack?: () => void
  onClose: () => void
  /** Omit for a body that brings its own buttons. */
  formId?: string
  submitLabel?: string
  submitting?: boolean
  error?: string | null
  onDelete?: () => void
  children: ReactNode
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--fl-surface)]">
      <div className="flex items-center gap-1 border-b border-[var(--fl-divider)] py-2 pl-1 pr-2">
        {onBack ? (
          <button
            type="button"
            aria-label="Back to choices"
            onClick={onBack}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)] md:hidden"
          >
            <ArrowLeft size={20} />
          </button>
        ) : null}
        <span className={cn(!onBack && 'pl-3', onBack && 'md:pl-3')}>
          <KindTile Icon={Icon} tone={tone} size="sm" />
        </span>
        <h2 className="ml-1.5 flex-1 font-serif text-xl font-semibold text-[var(--fl-heading)]">
          {title}
        </h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cn(
            'grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]',
            onBack && 'hidden md:grid'
          )}
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">{children}</div>
      {formId && (
        <div className="border-t border-[var(--fl-divider)] px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          {error && <p className="mb-2 text-xs text-[var(--fl-error)]">{error}</p>}
          <div className="flex gap-2.5">
            {onDelete && (
              <Button
                type="button"
                variant="outline"
                onClick={onDelete}
                className="h-12 rounded-xl px-5 font-semibold text-[var(--fl-error)] hover:text-[var(--fl-error)]"
              >
                Delete
              </Button>
            )}
            <Button
              type="submit"
              form={formId}
              disabled={submitting}
              className="h-12 flex-1 rounded-xl text-[15px] font-semibold"
            >
              {submitting ? 'Saving…' : submitLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Category Listbox: styled to match the platform's own inputs, rather than
// the browser's native <select> chrome, and filtered to only the categories matching the
// transaction's current Income/Expense type so a mismatched category can never be selected. ---

export function CategoryListbox({
  categories,
  type,
  value,
  onChange,
}: {
  categories: Category[]
  type: 'INCOME' | 'EXPENSE'
  value: string
  onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const options = categories.filter((c) => c.type === type)
  const selected = options.find((c) => c.id === value)

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        aria-label="Category"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="h-auto w-full items-center justify-between gap-2 rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 py-2.5 text-sm font-normal hover:bg-[var(--fl-surface)]"
      >
        <span className={selected ? 'text-[var(--fl-text-1)]' : 'text-[var(--fl-text-2)]'}>
          {selected ? selected.name : 'Select category'}
        </span>
        <ChevronDown size={16} className="text-[var(--fl-text-2)] shrink-0" />
      </Button>
      {open && (
        <ul
          role="listbox"
          aria-label="Category options"
          className={`${MENU_SURFACE} absolute z-10 mt-1 w-full max-h-56 overflow-y-auto`}
        >
          {options.map((c) => (
            <li key={c.id}>
              <Button
                type="button"
                variant="ghost"
                onMouseDown={() => {
                  onChange(c.id)
                  setOpen(false)
                }}
                className={[
                  'h-auto w-full justify-start rounded-none px-3 py-2 text-left text-sm font-normal hover:bg-[var(--fl-surface-hover)] hover:text-[var(--fl-text-1)]',
                  c.id === value
                    ? 'bg-[var(--fl-surface-hover)] font-medium text-[var(--fl-text-1)]'
                    : 'text-[var(--fl-text-1)]',
                ].join(' ')}
              >
                {c.name}
              </Button>
            </li>
          ))}
          {options.length === 0 && (
            <li className="px-3 py-2 text-sm text-[var(--fl-text-2)]">
              No {type === 'INCOME' ? 'income' : 'expense'} categories yet
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

// --- Transaction Link Combobox: searchable picker backing both the refund's
// "which expense" field and a compensation INCOME's optional "link to a transaction"
// field. `refundableOnly` restricts results to EXPENSE rows with money left to refund. ---

export function TransactionLinkCombobox({
  value,
  label,
  onChange,
  refundableOnly,
  type,
  placeholder = 'Search by description…',
}: {
  value: LinkedTransactionOption | null
  label: string
  onChange: (option: LinkedTransactionOption | null) => void
  refundableOnly?: boolean
  type?: string
  placeholder?: string
}) {
  const { format } = useFormatCurrency()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const debouncedQuery = useDebouncedValue(query, 250)

  const { data: options } = useQuery({
    queryKey: ['transaction-link-search', debouncedQuery, refundableOnly ?? false, type ?? ''],
    queryFn: () => searchLinkableTransactions(debouncedQuery, { refundableOnly, type }),
    enabled: open && debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  })

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 py-2.5 text-sm">
        <div className="min-w-0">
          <p className="truncate font-medium text-[var(--fl-text-1)]">{value.description}</p>
          <p className="text-xs text-[var(--fl-text-2)]">
            {formatDate(value.date)} · {format(value.amount)}
            {refundableOnly && value.remainingRefundableAmount != null
              ? ` · ${format(value.remainingRefundableAmount)} refundable`
              : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          tooltip={`Clear ${label}`}
          onClick={() => onChange(null)}
          className="shrink-0 text-[var(--fl-text-3)]"
        >
          <X size={14} />
        </Button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && query.trim().length >= 2 && (
        <ul className={`${MENU_SURFACE} absolute z-10 mt-1 w-full max-h-56 overflow-y-auto`}>
          {options && options.length > 0 ? (
            options.map((o) => (
              <li key={o.id}>
                <Button
                  type="button"
                  variant="ghost"
                  onMouseDown={() => {
                    onChange(o)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="h-auto w-full items-center justify-between gap-2 rounded-none px-3 py-2 text-left text-sm font-normal hover:bg-[var(--fl-surface-hover)]"
                >
                  <span className="min-w-0 truncate font-medium text-[var(--fl-text-1)]">
                    {o.description}
                  </span>
                  <span className="shrink-0 text-xs text-[var(--fl-text-2)]">
                    {formatDate(o.date)} · {format(o.amount)}
                  </span>
                </Button>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-sm text-[var(--fl-text-2)]">
              {options ? 'No matches' : 'Searching…'}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
